import auth from '@/middleware/auth'
import { user } from '@/api'
import store from '@/store'
import type { RouteLocationNormalized, NavigationGuardNext, RouteLocationRaw } from 'vue-router'

type DashboardAccessContext = {
  to: RouteLocationNormalized
  from: RouteLocationNormalized
  next: NavigationGuardNext
}

const canAccessDashboard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): Promise<boolean> => {
  const company = to.params.company as string

  // Default.vue resets the store only after a company switch has navigated,
  // so a profile loaded for the previous company says nothing about this one.
  if (from.params.company && from.params.company !== company) {
    const { data } = await user.getCurrentUser(company)
    return data.data.canAccessDashboard ?? true
  }

  // The account-scoped profile (Home/Activity surface) carries no dashboard
  // flag; isLoaded flips only after the company-scoped profile request.
  if (!(store as any).state.user.currentUser || !(store as any).state.user.isLoaded) {
    await (store as any).dispatch('user/getCurrentUser', company)
  }

  return (store as any).getters['user/canAccessDashboard']
}

// A private dashboard is only open to the users on its access list
// (Admin > Dashboard). nextFactory resolves the navigation on the first
// middleware's next(), so a redirect from a guard chained after auth is
// dropped: this guard is the route's only middleware and runs auth itself.
const dashboardAccess = async ({ to, from, next }: DashboardAccessContext): Promise<void> => {
  const authRedirect = await new Promise<RouteLocationRaw | undefined>((resolve) => {
    auth({ to, next: resolve as NavigationGuardNext })
  })

  if (authRedirect) {
    return next(authRedirect)
  }

  try {
    if (await canAccessDashboard(to, from)) {
      return next()
    }
  } catch {
    // The profile request failed; keep the dashboard closed rather than open
    // it on a guess. httpClient has already reported the error.
  }

  return next({ name: 'pre-orders', params: { company: to.params.company } })
}

export default dashboardAccess
